import React, { useState } from "react"
import lobbyBg from "@/images/backgrounds/lobby-background.webp"
import LandingPageNavbar from "@/Components/LandingPageNavbar"

interface TabData {
  id: string
  title: string
  content: React.ReactNode
}

const Rules: React.FC = () => {
  const [activeTab, setActiveTab] = useState("roles")

  const tabs: TabData[] = [
    {
      id: "roles",
      title: "Roles",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">Game Roles</h3>
          <p className="text-[#4a2e19] text-lg mb-6 leading-relaxed">
            Choose from 7 unique roles at the start of each game. Each role
            provides special advantages throughout the entire game.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">💰</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Financer
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Start with +$500 cash</li>
                <li>• Gain +$300 at GO ($500 total)</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🏠</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Constructor
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• 50% cheaper building costs</li>
                <li>• Faster property development</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🎭</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Trickster
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Draw 2 action cards instead of 1</li>
                <li>• More strategic options</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🏦</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Rent Lord
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Double rent on all properties</li>
                <li>• Massive income potential</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🧾</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Auction Strategist
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Pay $100 less on every winning bid</li>
                <li>• More competitive bidding</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">📈</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Wall Street Wolf
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Double returns on stocks</li>
                <li>• Superior investment strategy</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b] md:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🔒</span>
                <h4 className="text-xl font-semibold text-[#4a2e19]">
                  Private Buyer
                </h4>
              </div>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Buy 3 properties at base price without auction</li>
                <li>• Once per property ability</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "auction",
      title: "Auction",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            Auction System
          </h3>
          <div className="space-y-4">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Universal Auction Rule
              </h4>
              <p className="text-[#4a2e19] leading-relaxed mb-3">
                <strong>
                  Every property automatically goes to auction when a player
                  lands on it
                </strong>
                , with only 2 exceptions.
              </p>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• No direct buying - all properties go to auction</li>
                <li>• All players can participate in bidding</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Auction Mechanics
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Hidden bidding - all bids placed simultaneously</li>
                <li>• One-time only - each auction happens exactly once</li>
                <li>• Starting bid is $1</li>
                <li>• Highest bidder wins the property</li>
                <li>• Unsold properties remain with the bank</li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Exceptions to Auction Rule
              </h4>
              <p className="text-[#4a2e19] leading-relaxed mb-3">
                Only 2 ways to buy properties directly without auction:
              </p>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Action Card Direct Buy</li>
                <li>
                  • Private Buyer Role (3 properties at base price, once per
                  property)
                </li>
              </ul>
            </div>

            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Strategy Tips
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Bid strategically to complete color sets</li>
                <li>• Don't overbid - save money for other opportunities</li>
                <li>• Watch other players' money to gauge bidding power</li>
                <li>• Use Private Buyer role wisely for valuable properties</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "action-cards",
      title: "Action Cards",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            Action Cards
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Chance Cards
              </h4>
              <p className="text-[#4a2e19] leading-relaxed mb-3">
                Blue cards that can have various effects:
              </p>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Move to specific locations</li>
                <li>• Collect or pay money</li>
                <li>• Go to Jail</li>
                <li>• Get out of Jail free</li>
                <li>• Pay taxes</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Community Chest
              </h4>
              <p className="text-[#4a2e19] leading-relaxed mb-3">
                Green cards with community-based effects:
              </p>
              <ul className="text-[#4a2e19] space-y-1 text-sm">
                <li>• Receive money from the bank</li>
                <li>• Pay money to the bank</li>
                <li>• Collect from other players</li>
                <li>• Move to Go</li>
                <li>• Birthday gifts</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Using Action Cards
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Draw when landing on Chance or Community Chest</li>
                <li>• Follow the card's instructions immediately</li>
                <li>• Some cards can be kept for later use</li>
                <li>• Return most cards to the bottom of the deck</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Strategic Use
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Save "Get Out of Jail Free" cards</li>
                <li>• Use movement cards strategically</li>
                <li>• Plan around potential money changes</li>
                <li>• Consider timing of card effects</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "trade",
      title: "Trade",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            Trading System
          </h3>
          <div className="space-y-4">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                What Can Be Traded
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Properties (with or without houses/hotels)</li>
                <li>• Money</li>
                <li>• Get Out of Jail Free cards</li>
                <li>• Combinations of the above</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Trading Rules
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Trades can only happen during your turn</li>
                <li>• Both players must agree to the trade</li>
                <li>• You cannot trade mortgaged properties</li>
                <li>• You cannot trade properties with houses on them</li>
                <li>• Trades are binding once accepted</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Trading Strategy
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Trade to complete color sets</li>
                <li>• Offer fair deals to encourage trading</li>
                <li>• Consider the value of properties and their potential</li>
                <li>• Use trades to avoid bankruptcy</li>
                <li>• Negotiate for better deals</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "stock-exchange",
      title: "Stock Exchange",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            Stock Exchange
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Buying Stocks
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Visit the Stock Exchange tile</li>
                <li>• Choose from available stocks</li>
                <li>• Pay the listed price per share</li>
                <li>• Stocks can be bought during your turn</li>
                <li>• You can buy multiple shares</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Selling Stocks
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Visit the Stock Exchange tile</li>
                <li>• Choose stocks you own to sell</li>
                <li>• Receive current market value</li>
                <li>• You can sell partial holdings</li>
                <li>• Stock prices fluctuate during the game</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Stock Values
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Prices change based on game events</li>
                <li>• Some properties affect stock prices</li>
                <li>• Action cards can impact stock values</li>
                <li>• Monitor market trends</li>
                <li>• Buy low, sell high strategy</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Investment Tips
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Diversify your portfolio</li>
                <li>• Watch for market opportunities</li>
                <li>• Don't invest all your money</li>
                <li>• Consider selling when values are high</li>
                <li>• Use stocks as emergency funds</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "buy-houses",
      title: "Buy Houses",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            Buying Houses & Hotels
          </h3>
          <div className="space-y-4">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Requirements
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• You must own all properties in a color set</li>
                <li>• Houses must be built evenly across the set</li>
                <li>• You cannot build more than 4 houses per property</li>
                <li>• You must have enough money to pay for houses</li>
                <li>• Properties cannot be mortgaged</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Building Rules
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Houses cost varies by property</li>
                <li>• Build during your turn</li>
                <li>• You can build multiple houses at once</li>
                <li>• Houses increase rent significantly</li>
                <li>• Hotels replace 4 houses</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Even Building Rule
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>
                  • You cannot have more than 1 house difference between
                  properties
                </li>
                <li>
                  • Example: If you have 2 houses on one property, you must have
                  at least 1 house on others
                </li>
                <li>• This rule applies to hotels as well</li>
                <li>• Plan your building strategy carefully</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Strategy
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Build on high-traffic properties first</li>
                <li>• Focus on completing color sets</li>
                <li>• Consider the cost vs. potential rent increase</li>
                <li>• Don't overextend your finances</li>
                <li>• Keep some money for emergencies</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "special-tiles",
      title: "Special Tiles",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            Special Tiles
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">Go</h4>
              <p className="text-[#4a2e19] leading-relaxed">
                Collect $200 when you pass or land on Go. This is your salary
                for completing a circuit of the board.
              </p>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Jail
              </h4>
              <p className="text-[#4a2e19] leading-relaxed">
                You can still collect rent while in jail, but you cannot move.
                Pay $50 or use a "Get Out of Jail Free" card to leave.
              </p>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Free Parking
              </h4>
              <p className="text-[#4a2e19] leading-relaxed">
                A safe space where nothing happens. Some house rules add money
                to this space from taxes and fees.
              </p>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Income Tax
              </h4>
              <p className="text-[#4a2e19] leading-relaxed">
                Pay 10% of your total worth or $200, whichever you prefer.
                Calculate your worth carefully!
              </p>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Luxury Tax
              </h4>
              <p className="text-[#4a2e19] leading-relaxed">
                Pay $75 to the bank. This is a fixed amount regardless of your
                wealth.
              </p>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Go To Jail
              </h4>
              <p className="text-[#4a2e19] leading-relaxed">
                Move directly to jail. Do not pass Go, do not collect $200. You
                can still collect rent while in jail.
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "end-game",
      title: "End Game",
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-[#a13a2b] mb-4">
            End Game Conditions
          </h3>
          <div className="space-y-4">
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Bankruptcy
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• When a player cannot pay their debts</li>
                <li>• All properties go to the creditor or bank</li>
                <li>• Houses and hotels are returned to the bank</li>
                <li>• Player is eliminated from the game</li>
                <li>• Game continues until one player remains</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Winning Conditions
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Be the last player remaining (all others bankrupt)</li>
                <li>• Have the highest total net worth</li>
                <li>• Control the most valuable properties</li>
                <li>• Successfully manage your money and investments</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Final Scoring
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• Count all cash on hand</li>
                <li>• Add value of all properties owned</li>
                <li>• Include value of houses and hotels</li>
                <li>• Add value of stocks and other assets</li>
                <li>• Subtract any outstanding debts</li>
              </ul>
            </div>
            <div className="bg-[#f5e2c3] bg-opacity-30 p-6 rounded-xl border-2 border-[#a13a2b]">
              <h4 className="text-xl font-semibold text-[#4a2e19] mb-3">
                Game Statistics
              </h4>
              <ul className="text-[#4a2e19] space-y-2">
                <li>• View detailed game statistics</li>
                <li>• See property ownership history</li>
                <li>• Track money flow throughout the game</li>
                <li>• Review trading and auction activity</li>
                <li>• Analyze strategic decisions</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        backgroundImage: `url(${lobbyBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Warm vignette overlay for blending */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#f5e2c3cc] via-[#e49e78bb] to-[#4a2e19ee] z-0" />

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Navbar */}
        <LandingPageNavbar showHomeLink={true} />

        {/* Main Content */}
        <div className="flex-1 pt-24 pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] pb-[max(2rem,var(--safe-bottom))]">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-5xl md:text-6xl font-extrabold text-[#a13a2b] mb-4 drop-shadow-lg font-fourth tracking-wider vintage-text-shadow">
                Game <span className="text-[#4a2e19]">Rules</span>
              </h1>
              <p className="text-xl text-[#4a2e19] max-w-2xl mx-auto leading-relaxed drop-shadow font-fourth vintage-text-shadow">
                Master the rules of Dealmaster and become the ultimate property
                tycoon
              </p>
            </div>

            {/* Tabs */}
            <div className="bg-[#f5e2c3] bg-opacity-50 rounded-2xl p-6 vintage-card">
              {/* Tab Navigation */}
              <div className="flex flex-wrap gap-2 mb-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${activeTab === tab.id
                        ? "bg-[#a13a2b] text-white shadow-lg"
                        : "bg-[#f5e2c3] text-[#4a2e19] hover:bg-[#e49e78] hover:text-white"
                      }`}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="min-h-[500px]">
                {tabs.find((tab) => tab.id === activeTab)?.content}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vintage style utility classes */}
      <style>{`
        .vintage-border { box-shadow: 0 4px 0 #A13A2B, 0 8px 24px #2A1209; }
        .vintage-text-shadow { text-shadow: 1px 2px 0 #2A1209, 0 2px 8px #4A2E19; }
        .vintage-card { 
          background: rgba(245, 226, 195, 0.1);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(161, 58, 43, 0.3);
        }
      `}</style>
    </div>
  )
}

export default Rules
